import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Custom hook to get local storage data with React Query
const useLocalStorageQuery = (key: any, queryKey: any) => {
  const queryClient = useQueryClient();

  // Fetch function to get data from local storage
  const fetchLocalStorageData = () => {
    const local_data = localStorage.getItem(key);
    return local_data ? JSON.parse(local_data) : null;
  };

  // React Query useQuery hook
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: fetchLocalStorageData,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Effect to listen for storage changes
  useEffect(() => {
    const handleStorageChange = (event: any) => {
      if (event.key === key) {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [key, queryKey, queryClient]);

  return query;
};

function isJsonString(str: any) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
function useLocalStorage(key: string, initialValue: any) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      if (isJsonString(item)) {
        return item ? JSON.parse(item) : initialValue;
      } else {
        return item;
      }
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: any) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(
        key,
        typeof valueToStore === "object"
          ? JSON.stringify(valueToStore)
          : valueToStore
      );
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.debug(error);
    }
  };

  return [storedValue, setValue];
}

export { useLocalStorage, useLocalStorageQuery };
